import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  Radio,
  RadioGroup,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  useToast,
  Text
} from "@chakra-ui/react";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Messages, PurposeType } from '../../contexts/constants';
import {
  GeneralPurposeChecklistData,
  PagePermissions,
  ProjectAdditionalInfo,
} from "../../contexts/types";
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { ProjectContext } from '../InvestmentScreening/InvestmentScreeningDetail';
import HelpIcon from "../InvestmentScreening/HelpIcon";

/**
 * モーダルのプロパティ
 */
interface Props {
  /** モーダルの表示/非表示 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** プロジェクトID */
  projectId: string;
  /** 汎用設備チェックリストデータ更新関数 */
  onUpdate: (data: GeneralPurposeChecklistData) => void;
  /** 画面パーミッション */
  pagePermissions?: PagePermissions;
  /** 目的コード */
  purpose?: string | null;
}

/**
 * 汎用設備チェックリストモーダル
 */
const GeneralPurposeChecklistModal: React.FC<Props> = ({
  isOpen,
  onClose,
  projectId,
  onUpdate,
  pagePermissions,
  purpose,
}) => {
  const { t } = useTranslation();
  /** 更新投資かどうか */
  const isUpdateInvestment = purpose === PurposeType.UpdateAndOther.code;
  /** タイトル */
  const TITLE = t('components.GeneralPurposeChecklistModal.title');
  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);
  /** 振り返りモード */
  const projectContext = useContext(ProjectContext);
  const reviewDate = projectContext?.reviewDate ?? null;
  /** トースト表示用 */
  const toast = useToast();
  /** 保存ボタン活性制御 */
  const [isSaving, setIsSaving] = useState(false);
  /** プロジェクト追加情報 */
  const [projectAdditionalInfo, setProjectAdditionalInfo] = useState<ProjectAdditionalInfo>({});

  /** フォームデータ */
  const [formData, setFormData] = useState<GeneralPurposeChecklistData>({
    groupTotalCount: "",
    siteCount: "",
    storageDepartmentApproval: "",
  });

  /** エラー状態 */
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  /**
   * モーダル表示時にデータを取得
   */
  useEffect(() => {
    if (isOpen && projectId) {
      fetchData();
    }
  }, [isOpen, projectId]);

  /**
   * データ取得
   */
  const fetchData = async () => {
    try {
      const response = await WebAPI.request({
        query: `
          query getProjectAdditionalInfoById($projectId: String, $reviewDate: String) {
            getProjectAdditionalInfoById(projectId: $projectId, reviewDate: $reviewDate) {
              projectId
              generalPurposeChecklistData
              updateDate
            }
          }
        `,
        variables: { projectId, ...(reviewDate && { reviewDate }) }
      });

      if (response?.data?.getProjectAdditionalInfoById) {
        const additionalInfo: ProjectAdditionalInfo = response.data.getProjectAdditionalInfoById;
        setProjectAdditionalInfo(additionalInfo);

        // 既存データがあれば読み込み
        if (additionalInfo.generalPurposeChecklistData) {
          try {
            const savedData: GeneralPurposeChecklistData = JSON.parse(additionalInfo.generalPurposeChecklistData);
            setFormData(savedData);
          } catch (e) {
            console.error("Failed to parse generalPurposeChecklistData:", e);
          }
        } else {
          // データがない場合は初期化
          setFormData({
            groupTotalCount: "",
            siteCount: "",
            storageDepartmentApproval: "",
          });
        }
      }
    } catch (error) {
      console.error("Failed to fetch project additional info:", error);
    }
  };

  /**
   * 入力変更ハンドラー
   */
  const handleInputChange = (field: keyof GeneralPurposeChecklistData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // エラーをクリア
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  /**
   * バリデーション
   */
  const validate = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.groupTotalCount.trim()) {
      newErrors.groupTotalCount = t('components.GeneralPurposeChecklistModal.validation.groupTotalRequired');
    }
    if (!formData.siteCount.trim()) {
      newErrors.siteCount = t('components.GeneralPurposeChecklistModal.validation.siteCountRequired');
    }
    if (isUpdateInvestment && !formData.storageDepartmentApproval) {
      newErrors.storageDepartmentApproval = t('components.GeneralPurposeChecklistModal.validation.storageDepartmentApprovalRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 保存処理
   */
  const handleSave = async () => {
    if (!validate()) {
      toast({
        title: TITLE,
        description: t('components.GeneralPurposeChecklistModal.validation.requiredFields'),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);

    try {
      const result = await WebAPI.request({
        query: `
          mutation putGeneralPurposeChecklist($input: GeneralPurposeChecklistInput!) {
            putGeneralPurposeChecklist(input: $input) {status}
          }
        `,
        variables: {
          input: {
            projectId: projectId,
            generalPurposeChecklistData: JSON.stringify(formData),
            updateDate: projectAdditionalInfo.updateDate || null,
            ...(reviewDate && { reviewDate }),
          }
        }
      });

      if (result?.data?.putGeneralPurposeChecklist?.status === "success") {
        toast({
          title: TITLE,
          description: t('components.GeneralPurposeChecklistModal.message.saveSuccess'),
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onUpdate(formData);
      } else if (result?.data?.putGeneralPurposeChecklist?.status === 'expired') {
        toast({
          title: TITLE,
          description: Messages.expiredMessage,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: TITLE,
          description: t('components.GeneralPurposeChecklistModal.message.saveFailed'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Failed to save general purpose checklist:", error);
      toast({
        title: TITLE,
        description: t('components.GeneralPurposeChecklistModal.message.saveFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * モーダルを閉じる際のリセット
   */
  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader bg="blue.600" color="white" borderTopRadius="md" py={3}>
          {TITLE}
        </ModalHeader>
        <ModalCloseButton color="white" />
        <ModalBody py={6}>
          <VStack spacing={0} align="stretch">
            <FormControl isRequired isInvalid={!!errors.groupTotalCount}>
              <Flex align="center" py={4} gap={2}>
                <FormLabel w="235px" mb={0} fontWeight="bold" flexShrink={0}>
                  {t('components.GeneralPurposeChecklistModal.field.groupTotalCount')}
                </FormLabel>
                <HelpIcon>{t('components.GeneralPurposeChecklistModal.tooltip.groupTotalCount')}</HelpIcon>
                <Input
                  flex={1}
                  placeholder={t('components.GeneralPurposeChecklistModal.placeholder.groupTotalExample')}
                  value={formData.groupTotalCount}
                  onChange={(e) => handleInputChange("groupTotalCount", e.target.value)}
                  isDisabled={!pagePermissions?.isEditable}
                />
              </Flex>
              {errors.groupTotalCount && (
                <FormErrorMessage mt={-2} mb={2}>{errors.groupTotalCount}</FormErrorMessage>
              )}
            </FormControl>

            <Divider />

            <FormControl isRequired isInvalid={!!errors.siteCount}>
              <Flex align="center" py={4} gap={2}>
                <FormLabel w="235px" mb={0} fontWeight="bold" flexShrink={0}>
                  {t('components.GeneralPurposeChecklistModal.field.siteCount')}
                </FormLabel>
                <HelpIcon>{t('components.GeneralPurposeChecklistModal.tooltip.siteCount')}</HelpIcon>
                <Input
                  flex={1}
                  placeholder={t('components.GeneralPurposeChecklistModal.placeholder.siteCountExample')}
                  value={formData.siteCount}
                  onChange={(e) => handleInputChange("siteCount", e.target.value)}
                  isDisabled={!pagePermissions?.isEditable}
                />
              </Flex>
              {errors.siteCount && (
                <FormErrorMessage mt={-2} mb={2}>{errors.siteCount}</FormErrorMessage>
              )}
            </FormControl>

            {isUpdateInvestment && (
              <>
                <Divider />
                <FormControl isRequired isInvalid={!!errors.storageDepartmentApproval}>
                  <Flex align="center" py={4}>
                    <FormLabel w="280px" mb={0} fontWeight="bold" flexShrink={0}>
                      {t('components.GeneralPurposeChecklistModal.field.storageDepartmentApproval')}
                    </FormLabel>
                    <RadioGroup
                      ml={3}
                      value={formData.storageDepartmentApproval}
                      onChange={(value) => handleInputChange("storageDepartmentApproval", value)}
                    >
                      {/* value はDB保存値のため日本語固定。表示のみ翻訳 */}
                      <Radio mr={4} isDisabled={!pagePermissions?.isEditable} value="はい">{t('components.GeneralPurposeChecklistModal.option.yes')}</Radio>
                      <Radio isDisabled={!pagePermissions?.isEditable} value="いいえ">{t('components.GeneralPurposeChecklistModal.option.no')}</Radio>
                    </RadioGroup>
                  </Flex>
                  {errors.storageDepartmentApproval && (
                    <FormErrorMessage mt={-2} mb={2}>{errors.storageDepartmentApproval}</FormErrorMessage>
                  )}
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <Divider />

        <ModalFooter py={3}>
          <Button
            colorScheme="blue"
            mr={3}
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={!pagePermissions?.isEditable}
          >
            {t('components.GeneralPurposeChecklistModal.button.save')}
          </Button>
          <Button variant="ghost" onClick={handleClose}>
            {t('components.GeneralPurposeChecklistModal.button.close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GeneralPurposeChecklistModal;
